import { atom, useAtom } from "jotai";
import { debounce } from "lodash";
import React, { Suspense, useRef } from "react";
import { FormProvider } from "react-hook-form";

import {
  AvailableIntegrationFragment,
  ConnectionBaseFragment,
  IntegrationConfigurationFragment,
  useIntegrationConfigurationSuspenseQuery,
} from "@/apollo/types";
import { Alert, AlertIcon } from "@/components/elements/Alert";
import { ActionButton } from "@/components/elements/Button";
import confirm from "@/components/elements/Confirm";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import {
  SlideOver,
  SlideOverBody,
  SlideOverCloseButton,
  SlideOverFooter,
  SlideOverHeader,
} from "@/components/elements/SlideOver";
import { useCallbackRef } from "@/hooks/useCallbackRef";
import { useMountEffect } from "@/hooks/useMountEffect";
import {
  IntegrationLogo,
  useCreateConnection,
  useIntegration,
  useIntegrationDocumentationUrl,
} from "@/integrations";
import { useMixpanel } from "@/monitoring/mixpanel";
import ConnectionForm, {
  ConnectionFormSubmitButton,
  useConnectionForm,
} from "@/pages/Connections/ConnectionForm/ConnectionForm";
import { useToast } from "@/providers/ToastProvider";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

type SlideOverProps = Pick<
  React.ComponentProps<typeof SlideOver>,
  "show" | "onClose" | "size"
>;

type NewConnectionSlideOverProps = {
  integrationId?: string;
  onConnectionAdded?: (connection: ConnectionBaseFragment) => void;
  onConnectionFailed?: (error: Error) => void;
};

export function NewConnectionSlideOver(
  props: NewConnectionSlideOverProps & SlideOverProps,
) {
  const mixpanel = useMixpanel();
  const toast = useToast();

  const searchInputRef = useRef<HTMLInputElement>(null);

  const [isConfigurationDirty, setIsConfigurationDirty] = React.useState(false);

  const integration = useIntegration(props.integrationId);

  const handleConnectionAdded = (connection: ConnectionBaseFragment) => {
    props.onConnectionAdded?.(connection);
    mixpanel.track("Connector Setup Succeeded", {
      connection,
      integration_id: connection.integrationId,
    });
    props.onClose();
  };

  const { loading, createConnection } = useCreateConnection({
    onSuccess(connection) {
      handleConnectionAdded(connection);
    },
    onError(error, integrationId) {
      props.onConnectionFailed?.(error);
      toast("Connector not created", error.message, "error");
      mixpanel.track("Connector Setup Failed", {
        error: error.message,
        integration_id: integrationId,
      });
    },
  });

  return (
    <SlideOver
      show={props.show}
      size={props.size ?? "lg"}
      onClose={async () => {
        const onClose = () => {
          setIsConfigurationDirty(false);
          props.onClose();
        };

        if (isConfigurationDirty) {
          const confirmed = await confirm({
            title: "Unsaved Changes",
            message: "Are you sure you want to discard your changes?",
            type: "danger",
          });
          if (confirmed) {
            onClose();
          }
        } else {
          onClose();
        }
      }}
      initialFocus={searchInputRef}
    >
      <SlideOverCloseButton />

      <Suspense
        fallback={
          <div className="flex justify-center p-8 pt-20">
            <div className="flex items-center gap-4">
              <LoadingSpinner />
              <span className="text-muted-foreground">
                Loading connector configuration...
              </span>
            </div>
          </div>
        }
      >
        {props.integrationId && (
          <>
            {integration ? (
              <ConfigureConnectorView
                integration={integration}
                onSubmit={(integration, data) => {
                  createConnection(integration, data);
                }}
                isLoading={loading}
                onCancel={() => props.onClose()}
                onDirtyChange={setIsConfigurationDirty}
              />
            ) : (
              <div className="p-8 pt-20 text-center text-muted-foreground">
                <p>Could not load connector configuration.</p>
              </div>
            )}
          </>
        )}
      </Suspense>
    </SlideOver>
  );
}

function ConfigureConnectorView(props: {
  integration: AvailableIntegrationFragment;
  onSubmit: (
    integration: IntegrationConfigurationFragment,
    data: Record<string, any> & { label: string },
  ) => void;
  isLoading: boolean;
  onCancel: () => void;
  onDirtyChange: (isDirty: boolean) => void;
}) {
  const { data } = useIntegrationConfigurationSuspenseQuery({
    variables: {
      id: props.integration.aliasMetadata?.id || props.integration.id,
    },
  });
  const integrationConfig = data.integrationConfiguration;

  const mixpanel = useMixpanel();

  const { setConfig, getConfig } =
    useConnectionConfigCache<Record<string, any>>();
  const formMethods = useConnectionForm(integrationConfig, {
    defaultValues: getConfig(props.integration.id),
  });

  const { watch } = formMethods;
  const { isDirty } = formMethods.formState;

  const onDirtyChange = useCallbackRef(props.onDirtyChange);
  React.useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const setConnectionConfigDebounced = React.useMemo(() => {
    return debounce((integrationId: string, data: any) => {
      setConfig(integrationId, data);
    }, 200);
  }, [setConfig]);

  React.useEffect(() => {
    const subscription = watch((data) => {
      setConnectionConfigDebounced(props.integration.id, data);
    });
    return () => subscription.unsubscribe();
  }, [setConnectionConfigDebounced, props.integration.id, watch, setConfig]);

  const documentationUrl = useIntegrationDocumentationUrl(
    props.integration.id,
    false,
  );

  useMountEffect(() => {
    mixpanel.time_event("Connector Setup Succeeded");
    mixpanel.track("Connector Setup Form Viewed", {
      integration_id: props.integration.id,
    });
  });

  return (
    <FormProvider {...formMethods}>
      <SlideOverHeader className="flex items-center">
        <div className="flex items-center space-x-2">
          <IntegrationLogo id={props.integration.id} className="h-5" />
          <span>{props.integration.name}</span>
        </div>
        {documentationUrl != null && (
          <a
            href={documentationUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => {
              mixpanel.track("Connector Setup Documentation Viewed", {
                integration_id: props.integration.id,
              });
            }}
            className="ml-10 inline-flex items-center text-sm underline"
          >
            View setup guide
            <ArrowTopRightOnSquareIcon className="ml-1 h-4 w-4" />
          </a>
        )}
      </SlideOverHeader>
      <SlideOverBody>
        {props.integration.aliasMetadata && (
          <Alert status="info" className="mb-4">
            <AlertIcon />
            <div>
              The {props.integration.name} connector is available via{" "}
              <strong>{props.integration.aliasMetadata.name}</strong>. Please
              view the documentation for more information.
            </div>
          </Alert>
        )}
        <form
          id="integration-form"
          onSubmit={formMethods.handleSubmit((values) =>
            props.onSubmit(integrationConfig, values),
          )}
          className="flex h-full flex-col"
        >
          <ConnectionForm
            integration={integrationConfig}
            isLoading={props.isLoading}
          />
        </form>
      </SlideOverBody>
      <SlideOverFooter>
        <ActionButton colorScheme="secondary" onClick={() => props.onCancel()}>
          Cancel
        </ActionButton>
        <ConnectionFormSubmitButton
          integration={integrationConfig}
          isLoading={props.isLoading}
        />
      </SlideOverFooter>
    </FormProvider>
  );
}

/**
 * Small cache to store connection configuration data
 * added by the user. This is used to prefill the form
 * when the user goes back to the connection configuration
 */
const connectionConfigCache = atom<Record<string, any>>({});

function useConnectionConfigCache<T>(): {
  setConfig: (integrationId: string, config: T) => void;
  getConfig: (integrationId: string) => T | undefined;
} {
  const [cache, setCache] = useAtom(connectionConfigCache);

  const setConfig = React.useCallback(
    (integrationId: string, config: T) => {
      setCache((prev) => ({ ...prev, [integrationId]: config }));
    },
    [setCache],
  );

  const getConfig = React.useCallback(
    (integrationId: string) => {
      return cache[integrationId];
    },
    [cache],
  );

  return { setConfig, getConfig };
}
